import React, { useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import HEXLongArrowLeft from '@icons/hexpert/HEXLongArrowLeft';
import { HexpertModalBlock } from '../blocks/HexpertModalBlock';
import { HexpertRequestQuoteBlock } from '../blocks/HexpertRequestQuoteBlock';
import { WeFitDocumentBlock } from '../blocks/WeFitDocumentBlock';
import { WeFitLoadingBlock } from '../blocks/WeFitLoadingBlock';
import {
  ProductCaseStudy,
  ProductIntro,
  ProductImages,
  ProductListItem,
  ProductSummary,
  TopFeature,
} from '../components/ProductResult';
import { Separator } from '../components/Separator';
import { HexpertContext } from '../contexts/HexPertProvider';
import { FormType } from '../types/hexpertTypes';
import { validateEmail } from '../helpers/emailRegex';
import { imageUrlHelper } from '../helpers/imageHelper';
import { generatePdfDocument } from './HexpertResultPdf/HexpertResultPdf';
import './HexpertResultPage.scss';
import dataLayer from '@helpers/dataLayer';
import { generateTransactionId } from '@helpers/generateTransactionId';

const HexpertResultPage = () => {
  const [cookies, setCookie] = useCookies(['email_confirmed']);
  const [email, setEmail] = useState<string>(cookies?.email_confirmed ?? '');
  const { state, dispatch } = useContext(HexpertContext);
  const [forceEmailUrl, setForceEmailUrl] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showRequestQuote, setShowRequestQuote] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  useEffect(() => {
    if (state?.hexpertSteps === null) {
      dispatch({ type: "setHexpertSteps", payload: ['hexpert', `Hexpert-${state?.currentForm?.title}`] })
    }
  },[])
  useEffect(() => {
    if (state?.productResult?.redirectUrl && state?.hexpertSteps) {
      if (window) {
        if (window?.location?.search?.includes('show-request-quote=true')) {
          setShowRequestQuote(true);
          window.history.replaceState(
            '',
            '',
            state?.productResult?.redirectUrl + '&show-request-quote=true'
          );
        } else {
          window.history.replaceState('', '', state?.productResult?.redirectUrl);
          const resultTitle = state?.productResult?.ProductDetails?.Title;
          dataLayer.push('page_view', {
            page_path: `${state?.hexpertSteps?.join('/')}/${resultTitle}`,
            page_title: `Hexpert-${state?.currentForm?.title} - ${resultTitle}`,
            page_category: [...(state?.hexpertSteps || []), resultTitle],
            product_name: resultTitle,
            transaction_id: generateTransactionId(true, state?.currentForm?.id),
          });
        }
      }
    }
  }, [state?.productResult?.redirectUrl, state?.hexpertSteps]);

  const submitModal = () => {
    if (validateEmail(email)) {
      !showEmailModal && setForceEmailUrl(true);
      setCookie('email_confirmed', email);
      setShowEmailModal(false);
      dispatch({ type: 'isInitialForceModal', payload: false });

      // Create a clean url (avoiding multiple show-request-quote)
      const params = new URLSearchParams(window?.location?.search);
      const shareId = params.get('sn');

      let pageUrl = window?.location?.origin + window?.location?.pathname;
      let search = '?sn=' + shareId + '&show-request-quote=true';

      generatePdfDocument({
        data: state.productResult,
        resultUrl: pageUrl + search,
        email: email,
        pageId: state.pageProps?.PageId?.toString(),
        title: state.productResult?.ProductDetails?.Title,
        productType: state.pageProps?.MappedForms?.find(
          (form) => form?.formGuid === state.productResult?.FormGuid
        ),
        state: state,
        internalMail: state.pageProps?.InternalEmail,
      });
    } else {
      setInvalidEmail(true);
    }
  };

  const video = state?.productResult?.Product?.VideoImages?.find((video) =>
    video.Url?.includes('embed')
  );

  const formType = state.pageProps?.MappedForms?.find(
    (form) => form?.formGuid === state.productResult?.FormGuid
  );

  useEffect(() => {
    dispatch({ type: 'setStartSteps', payload: 'end' });
    // Use state so that we can close the quote screen
    const showQuote = window?.location?.search?.includes('show-request-quote=true');
    setShowRequestQuote(showQuote);
  }, []);

  const hasResultUrl = window?.location?.search?.includes('?sn=');

  const cleanURL = () => {
    // clean up the url after leaving the result page
    const redirectURL = new URL(window?.location?.href);

    redirectURL.searchParams.delete('show-request-quote');
    if (hasResultUrl) {
      redirectURL.searchParams.delete('sn');
    }

    history.replaceState({}, '', redirectURL);
  };

  const requestQuoteBackClick = () => {
    cleanURL();

    setShowRequestQuote(false);
    if (!state?.productResult) {
      dispatch({ type: 'showProductResult', payload: false });
    }
  };

  const resultPageBackClick = () => {
    cleanURL();
    dispatch({ type: 'showProductResult', payload: false });
    dispatch({ type: 'setResult', payload: null });
  };

  return state.loadingResult ? (
    <WeFitLoadingBlock />
  ) : !state?.productResult ? (
    <HexpertRequestQuoteBlock
      pageProps={state.pageProps}
      onClose={requestQuoteBackClick}
      form={state.pageProps?.Forms.find((form) => form.Key === FormType.REQUEST_QUOTE)}
      guid={state.pageProps?.[FormType.REQUEST_QUOTE]}
      title={
        (state?.productResult?.Summary?.LocalizedTranslationResources?.WeFitProductNamePrefix ??
          '') + (state?.productResult?.ProductDetails?.Title ?? '')
      }
    />
  ) : showRequestQuote ? (
    <HexpertRequestQuoteBlock
      pageProps={state.pageProps}
      onClose={requestQuoteBackClick}
      form={state.pageProps?.Forms.find((form) => form.Key === FormType.REQUEST_QUOTE)}
      guid={state.pageProps?.[FormType.REQUEST_QUOTE]}
      shareUrl={state.productResult?.redirectUrl}
      title={
        (state?.productResult?.Summary?.LocalizedTranslationResources?.WeFitProductNamePrefix ??
          '') + (state?.productResult?.ProductDetails?.Title ?? '')
      }
    />
  ) : (
    <>
      {state?.pageProps?.Modal &&
        (state?.isInitialForceModal || !hasResultUrl || showEmailModal) &&
        (!Boolean(cookies?.email_confirmed) || forceEmailUrl || showEmailModal) && (
          <HexpertModalBlock
            ModalButtonText={state?.pageProps?.ModalButtonText}
            ModalConfirmationDescription={state?.pageProps?.ModalConfirmationDescription}
            ModalConfirmationHeading={state?.pageProps?.ModalConfirmationHeading}
            ModalDescription={state?.pageProps?.ModalDescription}
            ModalHeading={state?.pageProps?.ModalHeading}
            ModalImage={state?.pageProps?.ModalImage}
            ModalQuoteText={state?.pageProps?.ModalQuoteText}
            ModalTextboxPlaceholder={state?.pageProps?.ModalTextboxPlaceholder}
            ModalUsp={state?.pageProps?.ModalUsp}
            inputValue={email}
            onInputChange={(val) => {
              setInvalidEmail(false);
              setEmail(val?.target?.value ?? '');
            }}
            action={submitModal}
            isConfirm={Boolean(cookies?.email_confirmed) && forceEmailUrl}
            invalidEmail={invalidEmail}
          />
        )}
      <div className='c-hex-result-page-arrow' onClick={resultPageBackClick}>
        <HEXLongArrowLeft />
      </div>
      <div className='c-hex-result-page'>
        <p className='c-hex-result-page-subtitle'>
          {state?.productResult?.Summary?.LocalizedTranslationResources?.WeFitResultTitle}
        </p>
        <h1 className='c-hex-result-page-title'>
          {state?.productResult?.Summary?.LocalizedTranslationResources?.WeFitProductNamePrefix}
          {state?.productResult?.ProductDetails?.Title}
        </h1>
        <ProductImages
          title={state?.productResult?.ProductDetails?.Title}
          mainImage={state?.productResult?.ProductDetails?.ImageUrl}
          extraImages={state?.productResult?.Product?.Variant?.VideoImages}
        />
        <ProductIntro
          title={state?.productResult?.ProductDetails?.Title}
          description={state?.productResult?.ProductDetails?.Description}
          email={null}
          onClick={() => setShowRequestQuote(true)}
          quoteText={state?.productResult?.Product?.WefitProductRequestQuote}
          quoteQuestion={
            state?.productResult?.Summary?.LocalizedTranslationResources
              ?.WeFitSatisfiedWithYourProduct
          }
        />
        <TopFeature
          features={state.productResult.Product.Features.map((feature) => ({
            image: feature.Image,
            imageAltText: feature.ImageAltText,
            title: feature.Title,
            body: feature.Description,
          }))}
        />

        <div className='c-hex-result-page-content-wrapper c-hex-result-page-top-reasons'>
          <div className='c-hex-result-page-content-block'>
            {state.productResult?.Product?.HasTopReasons && (
              <>
                <h3 style={{ marginTop: 0 }}>
                  {state.productResult.Product?.WefitProductTopReasons}
                </h3>
                {state.productResult?.Product?.TopReasons?.map((reason, i) => (
                  <div key={i}>
                    <ProductListItem text={reason} />
                    <Separator size={32} />
                  </div>
                ))}
              </>
            )}
          </div>
          <div className='c-hex-result-page-content-block' style={{ gap: '24px' }}>
            {state.productResult?.Product?.HasDidYouKnow && (
              <div className='c-hex-result-page-did-you-know-wrapper'>
                <h3 className='c-hex-result-page-did-you-know-wrapper-header'>
                  {state.productResult.Product?.WefitProductDidYouKnow}
                </h3>
                <p className='c-hex-result-page-did-you-know-wrapper-text'>
                  {state.productResult?.Product?.DidYouKnowText}
                </p>
              </div>
            )}
            <Separator size={24} />
            <WeFitDocumentBlock
              links={state.productResult.Product.Links}
              title={
                state.productResult?.Summary?.LocalizedTranslationResources
                  ?.WeFitRelatedContentTitle
              }
            />
          </div>
        </div>
        {video && video?.Url && (
          <div className='c-hex-result-page-content-wrapper c-hex-result-page-video-block'>
            <div
              className='c-hex-result-page-content-block'
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <iframe
                id='player'
                width='640'
                height='390'
                src={`${video?.Url}?controls=0`}
                allow='autoplay; encrypted-media'
                frameBorder='0'
              ></iframe>
            </div>
          </div>
        )}
        <ProductSummary
          buttonAction={() => dispatch({ type: 'showProductResult', payload: false })}
          data={state.productResult.Summary}
          typeName={formType?.title}
          resultIcon={formType?.resultIcon}
        />
        {state.productResult?.Product?.CaseStories?.length > 0 && (
          <>
            <h3 style={{ order: 10, marginTop: '80px' }}>
              {state.productResult?.Summary?.LocalizedTranslationResources?.WefitCaseStoriesTitle}
            </h3>
            {state.productResult?.Product?.CaseStories?.map((story, i) => (
              <ProductCaseStudy
                data={state.productResult.Summary}
                Description={story.Description}
                Image={imageUrlHelper(story?.Image)}
                ImageAltText={story?.ImageAltText}
                Title={story?.Title}
                ShowMore={story?.ShowMore}
                reverse={i % 2 === 0}
                key={i}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default HexpertResultPage;
